import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import format from 'date-fns/format';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { PagedServerTable } from '@components/Table';
import DatePicker from '@components/DatePicker';
import { PhoneNumberInput } from '@components/PhoneNumberInput';
import { getIconsByType, iconTypes } from '@services/icons';
import { copyToClipboard } from '@utils/clipboard';
import { isValidE164PhoneNumber } from '@utils/phoneNumberUtils';
import { useTranslation } from 'react-i18next';
import { ROLES } from '@containers/Admin/shared/roles';
import { USER_PAGE_TYPES } from '@containers/Admin/containers/UserPage/consts';
import { getInitials } from '@containers/Admin/containers/UsersTable/helpers';
import { ROUTES } from '@shared/constants';
import {
  MentorAvatar,
  MentorCell,
  MentorOverflow,
} from '@containers/Admin/containers/UsersTable/styles';
import {
  createInvite,
  deleteInvite,
  emailInvite,
  getInviteOptions,
  getInvites,
  updateInvite,
} from './services';
import {
  ActionButton,
  Actions,
  CheckboxRow,
  CommentMarker,
  DateCell,
  Field,
  FormGrid,
  Header,
  IssueMarker,
  Page,
  Panel,
  PrimaryButton,
  SecondaryButton,
  Status,
  Table,
  TableWrap,
  Td,
  Th,
  Title,
} from './styles';
import { getDateTime, getEventDayAndTime } from '@utils/timeHelpers';

const AddIcon = getIconsByType(iconTypes.ADD);
const EditIcon = getIconsByType(iconTypes.EDIT);
const DeleteIcon = getIconsByType(iconTypes.DELETE);
const MailIcon = getIconsByType(iconTypes.MAIL);
const CopyIcon = getIconsByType(iconTypes.CLIPBOARD);

export const INVITE_STATUSES = [
  'NEW',
  'SENT',
  'FAILED',
  'COMPLETED',
  'EXPIRED',
  // 'MANUAL_COMPLETION',
  // 'MANUAL_CANCEL',
  // 'MANUAL_EXTEND',
];

const INVITE_STATUS_TONES = {
  NEW: 'neutral',
  SENT: 'info',
  FAILED: 'error',
  COMPLETED: 'success',
  EXPIRED: 'error',
};

const parseInviteDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const rawValue = typeof value === 'object' ? value.at || value.date || value.value : value;
  const numericValue = typeof rawValue === 'number' || /^\d+$/.test(String(rawValue))
    ? Number(rawValue)
    : null;
  const timestamp = numericValue === null
    ? rawValue
    : numericValue < 100000000000 ? numericValue * 1000 : numericValue;
  const date = new Date(timestamp);

  return Number.isNaN(date.getTime()) ? null : date;
};

const defaultExpiration = () => {
  const value = new Date();
  value.setDate(value.getDate() + 14);
  return value;
};

const emptyForm = () => ({
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  courseId: '',
  mentorIds: [],
  expiresAt: defaultExpiration(),
  programExpiresAt: null,
  comment: '',
  sendEmail: false,
});

const toForm = (invite) => ({
  email: invite.email || '',
  firstName: invite.firstName || '',
  lastName: invite.lastName || '',
  phoneNumber: invite.phoneNumber || '',
  courseId: invite.courseId || '',
  mentorIds: invite.mentorIds || [],
  expiresAt: parseInviteDate(invite.expiresAt),
  programExpiresAt: parseInviteDate(invite.programExpiresAt),
  comment: invite.comment || '',
  status: invite.status || 'NEW',
  sendEmail: false,
});

const displayStatus = (status = '') => status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
const displayDate = (value) => {
  const date = parseInviteDate(value);
  return date ? format(date, 'DD/MM/YYYY') : '-';
};
const hasErrors = (result) => Boolean(result?.errors?.length);
const getError = (error) => error?.message || 'The request could not be completed.';
const toIsoDate = (value) => value ? new Date(value).toISOString() : null;
const EMPTY_OPTIONS = { courses: [], mentors: [] };
const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': { fontFamily: 'inherit', fontSize: 14 },
    },
  },
};

const Invites = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { roles = [] } = useSelector((state) => state.getIn(['app', 'authentication', 'user']).toJS());
  const isTechAdmin = roles.includes(ROLES.TECH_ADMIN);
  const [busy, setBusy] = useState('');
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [form, setForm] = useState(emptyForm());
  const [tableRevision, setTableRevision] = useState(0);
  const [inviteUrl, setInviteUrl] = useState('');
  const [attemptsInvite, setAttemptsInvite] = useState(null);

  useEffect(() => {
    let active = true;
    getInviteOptions()
      .then((result) => {
        if (active) setOptions(result);
      })
      .catch((error) => {
        if (active) toast.error(getError(error));
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });
    return () => { active = false; };
  }, []);

  const fetchData = useCallback((request) => getInvites(request), []);
  const handleTableError = useCallback((error) => toast.error(getError(error)), []);

  const openCreate = () => {
    if (isTechAdmin) return;
    setEditing(null);
    setForm(emptyForm());
  };

  const openEdit = (invite) => {
    if (isTechAdmin) return;
    setEditing(invite);
    setForm(toForm(invite));
  };

  const closeEditor = () => {
    if (!busy) setEditing(undefined);
  };

  const onChange = ({ target }) => setForm((current) => ({
    ...current,
    [target.name]: target.type === 'checkbox' ? target.checked : target.value,
  }));

  const normalizedPhoneNumber = form.phoneNumber.trim();
  const phoneNumberValid = !normalizedPhoneNumber || isValidE164PhoneNumber(normalizedPhoneNumber);

  const save = async () => {
    if (isTechAdmin) return;
    if (!form.email.trim() || !form.firstName.trim() || !form.lastName.trim() || !form.expiresAt || !phoneNumberValid) return;
    const input = {
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phoneNumber: normalizedPhoneNumber || null,
      courseId: form.courseId || null,
      mentorIds: form.mentorIds,
      expiresAt: toIsoDate(form.expiresAt),
      programExpiresAt: toIsoDate(form.programExpiresAt),
      comment: form.comment || null,
      ...(editing ? { status: form.status } : { sendEmail: form.sendEmail }),
    };
    setBusy('save');
    try {
      const result = editing
        ? await updateInvite(editing.id, input)
        : await createInvite(input);
      if (hasErrors(result)) throw new Error(result.errors[0].message);
      toast.success(editing ? 'Invite updated.' : 'Invite created.');
      setEditing(undefined);
      setTableRevision((revision) => revision + 1);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy('');
    }
  };

  const remove = async (invite) => {
    if (isTechAdmin) return;
    if (!window.confirm(`Delete the invite for ${invite.email}?`)) return;
    setBusy(invite.id);
    try {
      const result = await deleteInvite(invite.id);
      if (hasErrors(result)) throw new Error(result.errors[0].message);
      toast.success('Invite deleted.');
      setTableRevision((revision) => revision + 1);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy('');
    }
  };

  const send = async (invite) => {
    if (isTechAdmin) return;
    setBusy(invite.id);
    try {
      const result = await emailInvite(invite.id);
      if (hasErrors(result)) throw new Error(result.errors[0].message);
      toast.success('Invite email sent.');
      setTableRevision((revision) => revision + 1);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy('');
    }
  };

  const openUserRecord = (userId) => {
    if (!userId) return;
    history.push(generatePath(ROUTES.USERS.PAGE, {
      id: userId,
      pageType: USER_PAGE_TYPES.INFORMATION,
    }));
  };

  const copyUrl = async () => {
    const copied = await copyToClipboard(inviteUrl);
    toast[copied ? 'success' : 'error'](copied ? 'Invite URL copied.' : 'Could not copy the invite URL.');
  };

  const courseNamesById = useMemo(() => new Map(
    options.courses.map((course) => [String(course.id), course.name]),
  ), [options.courses]);
  const mentorNamesById = useMemo(() => new Map(
    options.mentors.map((mentor) => [String(mentor.id), mentor.fullName]),
  ), [options.mentors]);

  const columns = useMemo(() => [
    {
      key: 'createdAt',
      title: 'Created',
      width: 110,
      sortable: true,
      sortField: 'CREATION_DATE',
      defaultSortDirection: 'DESC',
      render: (invite) => {
        const parsedCreatedAt = parseInviteDate(invite.createdAt);
        if (!parsedCreatedAt) return '-';
        const createdAt = getEventDayAndTime(parsedCreatedAt);
        return (
          <DateCell>
            {createdAt.day}
            <small>{createdAt.time}</small>
          </DateCell>
        );
      },
    },
    {
      key: 'user',
      title: 'User',
      width: 200,
      filter: { type: 'text', placeholder: 'Search by user' },
      filterField: 'email',
      render: (invite) => (
        <span title={`${invite.firstName || ''} ${invite.lastName || ''} ${invite.email || ''}`}>
          <strong>{`${invite.firstName || ''} ${invite.lastName || ''}`.trim()}</strong><br />
          {invite.email}
        </span>
      ),
    },
    {
      key: 'phoneNumber',
      title: <PhoneOutlinedIcon aria-label="Phone" sx={{ fontSize: 18 }} />,
      width: 50,
      align: 'center',
      render: (invite) => invite.phoneNumber?.trim() ? (
        <Tooltip title={invite.phoneNumber} arrow>
          <CommentMarker><CheckIcon fontSize="inherit" /></CommentMarker>
        </Tooltip>
      ) : null,
    },
    {
      key: 'course',
      title: 'Course',
      width: 140,
      render: (invite) => courseNamesById.get(String(invite.courseId)) || invite.courseId || '-',
    },
    {
      key: 'mentors',
      title: 'Mentor',
      width: 115,
      filter: {
        type: 'multi-select',
        options: options.mentors.map((mentor) => ({
          value: String(mentor.id),
          label: mentor.fullName,
        })),
        searchable: true,
      },
      filterField: 'mentorIds',
      render: (invite) => {
        const mentors = (invite.mentorIds || []).map((id) => ({
          id,
          name: mentorNamesById.get(String(id)) || id,
        }));
        if (!mentors.length) return '-';
        return (
          <Tooltip
            title={mentors.map(({ id, name }) => <div key={id}>{name}</div>)}
            placement="bottom"
            arrow
          >
            <MentorCell>
              <MentorAvatar>{getInitials(mentors[0].name)}</MentorAvatar>
              {mentors.length > 1 && <MentorOverflow>{`+${mentors.length - 1}`}</MentorOverflow>}
            </MentorCell>
          </Tooltip>
        );
      },
    },
    {
      key: 'expiresAt',
      title: 'Expires',
      width: 100,
      sortable: true,
      sortField: 'EXPIRATION_DATE',
      render: (invite) => displayDate(invite.expiresAt),
    },
    {
      key: 'status',
      title: 'Status',
      width: 110,
      filter: {
        type: 'multi-select',
        options: INVITE_STATUSES.map((status) => ({ value: status, label: displayStatus(status) })),
      },
      render: (invite) => (
        <Status tone={INVITE_STATUS_TONES[invite.status] || 'neutral'}>
          {displayStatus(invite.status)}
        </Status>
      ),
    },
    {
      key: 'emailSent',
      title: 'Email sent',
      width: 110,
      render: (invite) => {
        const emailSentDates = (invite.emailSentDates || [])
          .map(parseInviteDate)
          .filter(Boolean)
          .sort((first, second) => second.getTime() - first.getTime());
        if (!emailSentDates.length) return '-';
        return (
          <Tooltip
            title={emailSentDates.map((date, index) => (
              <div key={`${date.getTime()}-${index}`}>{getDateTime(date.getTime())}</div>
            ))}
            placement="bottom"
            arrow
          >
            <span>{displayDate(emailSentDates[0])}</span>
          </Tooltip>
        );
      },
    },
    {
      key: 'issues',
      title: t('Invites.issues.title'),
      width: 75,
      align: 'center',
      render: (invite) => {
        const issues = invite.userAttempts?.[0]?.additionalInfo;
        return invite.status === 'COMPLETED' && issues?.length ? (
          <Tooltip
            title={issues.map((issue) => (
              <div key={issue}>{t(`Invites.issues.${issue}`)}</div>
            ))}
            arrow
          >
            <IssueMarker>
              <ErrorOutlineIcon fontSize="inherit" />
              <span>{issues.length}</span>
            </IssueMarker>
          </Tooltip>
        ) : null;
      },
    },
    {
      key: 'comment',
      title: <NotesOutlinedIcon aria-label="Comment" sx={{ fontSize: 18 }} />,
      width: 50,
      align: 'center',
      render: (invite) => invite.comment?.trim() ? (
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{invite.comment}</span>} arrow>
          <CommentMarker><CheckIcon fontSize="inherit" /></CommentMarker>
        </Tooltip>
      ) : null,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 210,
      render: (invite) => {
        const completed = invite.status === 'COMPLETED';
        const locked = completed || invite.status === 'FAILED';
        const rowBusy = busy === invite.id;
        const firstAttemptUserId = invite.userAttempts?.[0]?.userId;
        return (
          <Actions>
            <ActionButton title="Open first attempt user" aria-label="Open first attempt user" disabled={!firstAttemptUserId} onClick={() => openUserRecord(firstAttemptUserId)}><DescriptionOutlinedIcon /></ActionButton>
            <ActionButton title="View user attempts" aria-label="View user attempts" disabled={!invite.userAttempts?.length} onClick={() => setAttemptsInvite(invite)}><HistoryOutlinedIcon /></ActionButton>
            {!isTechAdmin && <ActionButton title="Edit invite" aria-label="Edit invite" disabled={locked || rowBusy} onClick={() => openEdit(invite)}><EditIcon /></ActionButton>}
            {!isTechAdmin && <ActionButton title="Send invite email" aria-label="Send invite email" disabled={locked || rowBusy} onClick={() => send(invite)}>{rowBusy ? <CircularProgress size={18} /> : <MailIcon />}</ActionButton>}
            <ActionButton title="Show invite URL" aria-label="Show invite URL" disabled={!invite.registrationUrl} onClick={() => setInviteUrl(invite.registrationUrl)}><CopyIcon /></ActionButton>
            {!isTechAdmin && <ActionButton title="Delete invite" aria-label="Delete invite" disabled={locked || rowBusy} onClick={() => remove(invite)}><DeleteIcon /></ActionButton>}
          </Actions>
        );
      },
    },
  ], [busy, courseNamesById, history, isTechAdmin, mentorNamesById, t]);

  const editorOpen = !isTechAdmin && editing !== undefined;

  return (
    <Page>
      <Panel>
        <Header>
          <Title>Invites</Title>
          {!isTechAdmin && (
            <PrimaryButton type="button" onClick={openCreate}>
              <AddIcon style={{ fontSize: 17, marginRight: 7, verticalAlign: 'middle' }} />
              New invite
            </PrimaryButton>
          )}
        </Header>
        <TableWrap>
          <PagedServerTable
            key={tableRevision}
            columns={columns}
            fetchData={fetchData}
            getRowKey={(invite) => invite.id}
            getRowStyle={(invite) => {
              const expirationDate = parseInviteDate(invite.expiresAt);
              return expirationDate && expirationDate.getTime() < Date.now() ? {
                opacity: 0.65,
                backgroundColor: '#f5f7fa',
              } : undefined;
            }}
            pageSize={10}
            initialSort={{ column: 'CREATION_DATE', direction: 'DESC' }}
            showFilterSummary
            itemLabel="invites"
            emptyMessage="No invites found."
            filteredEmptyMessage="No invites match these filters."
            loadingMessage="Loading invites..."
            errorMessage="Could not load invites."
            onError={handleTableError}
          />
        </TableWrap>
      </Panel>

      <Dialog open={editorOpen} onClose={closeEditor} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit invite' : 'New invite'}</DialogTitle>
        <DialogContent>
          <FormGrid>
            <Field>Email *<input name="email" type="email" value={form.email} onChange={onChange} /></Field>
            <Field>Phone number
              <PhoneNumberInput
                value={form.phoneNumber}
                onChange={(phoneNumber) => setForm((current) => ({ ...current, phoneNumber }))}
                placeholder={t('Authentication.MFA.phonePlaceholder')}
                ariaLabel={t('Authentication.MFA.phoneLabel')}
                error={!phoneNumberValid}
                helperText={!phoneNumberValid ? t('Authentication.MFA.phoneInvalid') : undefined}
              />
            </Field>
            <Field>First name *<input name="firstName" value={form.firstName} onChange={onChange} /></Field>
            <Field>Last name *<input name="lastName" value={form.lastName} onChange={onChange} /></Field>
            <Field>Course
              <FormControl fullWidth size="small">
                <Select name="courseId" value={form.courseId} onChange={onChange} displayEmpty disabled={optionsLoading} MenuProps={SELECT_MENU_PROPS} sx={{ fontSize: 14 }}>
                  <MenuItem value="">None</MenuItem>
                  {options.courses.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Field>
            <Field>Mentor
              <FormControl fullWidth size="small">
                <Select
                  name="mentorIds"
                  value={form.mentorIds}
                  onChange={onChange}
                  multiple
                  displayEmpty
                  disabled={optionsLoading}
                  MenuProps={SELECT_MENU_PROPS}
                  renderValue={(selected) => {
                    const selectedIds = Array.isArray(selected) ? selected : [selected];
                    return options.mentors
                      .filter((mentor) => selectedIds.includes(mentor.id))
                      .map((mentor) => mentor.fullName)
                      .join(', ') || 'None';
                  }}
                  sx={{ fontSize: 14 }}
                >
                  {options.mentors.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox
                        checked={form.mentorIds.includes(item.id)}
                        size="small"
                        disableRipple
                        sx={(theme) => ({
                          padding: '7px',
                          color: '#cfd8e3',
                          '&.Mui-checked': { color: theme.palette.novotalk.$activeColor },
                        })}
                      />
                      {item.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Field>
            {editing && <Field>Status<select name="status" value={form.status} onChange={onChange}>{INVITE_STATUSES.map((status) => <option key={status} value={status}>{displayStatus(status)}</option>)}</select></Field>}
            <Field>Invite expiration *
              <DatePicker
                value={form.expiresAt}
                onChange={(expiresAt) => setForm((current) => ({ ...current, expiresAt }))}
                format="DD/MM/YYYY"
                views={['year', 'month', 'day']}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Field>
            <Field>Program expiration
              <DatePicker
                value={form.programExpiresAt}
                onChange={(programExpiresAt) => setForm((current) => ({ ...current, programExpiresAt }))}
                format="DD/MM/YYYY"
                views={['year', 'month', 'day']}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Field>
            <Field data-wide="true">Comment<textarea name="comment" value={form.comment} onChange={onChange} /></Field>
            {!editing && (
              <Field data-wide="true">
                <CheckboxRow>
                  <Checkbox
                    checked={form.sendEmail}
                    onChange={({ target }) => setForm((current) => ({
                      ...current,
                      sendEmail: target.checked,
                    }))}
                    size="small"
                  />
                  <span>Send invite email after creation</span>
                </CheckboxRow>
              </Field>
            )}
          </FormGrid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton type="button" onClick={closeEditor} disabled={Boolean(busy)}>Cancel</SecondaryButton>
          <PrimaryButton type="button" onClick={save} disabled={Boolean(busy) || !form.email.trim() || !form.firstName.trim() || !form.lastName.trim() || !form.expiresAt || !phoneNumberValid}>
            {busy === 'save' ? <CircularProgress size={18} /> : 'Save'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(inviteUrl)} onClose={() => setInviteUrl('')} fullWidth maxWidth="sm">
        <DialogTitle>Invite URL</DialogTitle>
        <DialogContent>
          <TextField
            value={inviteUrl}
            fullWidth
            inputProps={{ readOnly: true }}
            onFocus={(event) => event.target.select()}
            sx={{ marginTop: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <SecondaryButton type="button" onClick={() => setInviteUrl('')}>Close</SecondaryButton>
          <PrimaryButton type="button" onClick={copyUrl}>
            <CopyIcon style={{ fontSize: 17, marginRight: 7, verticalAlign: 'middle' }} />
            Copy
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(attemptsInvite)} onClose={() => setAttemptsInvite(null)} fullWidth maxWidth="md">
        <DialogTitle>User attempts</DialogTitle>
        <DialogContent>
          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Email</Th>
                <Th>Status</Th>
                <Th>Info</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(attemptsInvite?.userAttempts || []).map((attempt) => (
                <tr key={`${attempt.userId}-${attempt.date}`}>
                  <Td>{parseInviteDate(attempt.date) ? getDateTime(parseInviteDate(attempt.date).getTime()) : '-'}</Td>
                  <Td>{attempt.email}</Td>
                  <Td><Status tone={INVITE_STATUS_TONES[attempt.status] || 'neutral'}>{displayStatus(attempt.status)}</Status></Td>
                  <Td>{attempt.additionalInfo?.map(displayStatus).join(', ') || '-'}</Td>
                  <Td><ActionButton title="Open user record" onClick={() => openUserRecord(attempt.userId)}><DescriptionOutlinedIcon /></ActionButton></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </DialogContent>
        <DialogActions>
          <SecondaryButton type="button" onClick={() => setAttemptsInvite(null)}>Close</SecondaryButton>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default Invites;